import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {meService} from "../core/services/me";
import {useMeStore} from "../core/store/me";
import {vehiclesApi} from "./api";
import type {
  CreateVehicleRequest,
  MergeVehicleRequest,
  MergeVehicleResponse,
  VehicleAccountabilityResponse,
  UpdateVehicleRequest,
  Vehicle,
  VehicleCatalogListResult,
  VehicleCreateFormData,
  VehicleMake,
  VehicleModel,
} from "./types";
import {
  normalizeVehicleCatalogMeta,
  normalizeVehicleMakesResponse,
  normalizeVehicleModelsResponse,
} from "./services/vehicle-catalog-normalize";

class VehiclesService {
  async getCreateFormData(): Promise<VehicleCreateFormData> {
    const {
      data: {data},
    } = await vehiclesApi.getCreateFormData();
    return data;
  }

  async getVehicles(options: ParamsOptions): Promise<GetListResponse<Vehicle>> {
    const {data} = await vehiclesApi.getVehicles(options);
    return {data: data.data, meta: data.meta};
  }

  async getVehicle(id: number, options?: ParamsOptions): Promise<Vehicle> {
    const {
      data: {data},
    } = await vehiclesApi.getVehicle(id, options);
    return data;
  }

  async getAccountability(
    vehicleId: number,
    householdId: number,
    contextPropertyId?: number,
  ): Promise<VehicleAccountabilityResponse> {
    const {
      data: {data},
    } = await vehiclesApi.getAccountability(vehicleId, householdId, contextPropertyId);
    return data;
  }

  async createVehicle(data: CreateVehicleRequest): Promise<Vehicle> {
    const {
      data: {data: vehicle},
    } = await vehiclesApi.createVehicle(data);
    return vehicle;
  }

  async updateVehicle(id: number, data: UpdateVehicleRequest): Promise<Vehicle> {
    const {
      data: {data: vehicle},
    } = await vehiclesApi.updateVehicle(id, data);
    return vehicle;
  }

  async deleteVehicle(id: number): Promise<void> {
    await vehiclesApi.deleteVehicle(id);
  }

  async mergeVehicle(
    winnerId: number,
    data: MergeVehicleRequest,
  ): Promise<MergeVehicleResponse> {
    const {
      data: {data: result},
    } = await vehiclesApi.mergeVehicle(winnerId, data);
    return result;
  }

  async getUnknownVehicles(options?: ParamsOptions): Promise<GetListResponse<Vehicle>> {
    const {data} = await vehiclesApi.getUnknownVehicles(options);
    return {data: data.data, meta: data.meta};
  }

  async searchVehicleMakes(
    nameContains: string,
    offset = 0,
  ): Promise<VehicleCatalogListResult<VehicleMake>> {
    const {data} = await vehiclesApi.getVehicleMakes(nameContains, offset);
    return {
      data: normalizeVehicleMakesResponse(data),
      meta: normalizeVehicleCatalogMeta(data),
    };
  }

  async searchVehicleModels(
    vehicleMakeId: number | null | undefined,
    nameContains?: string,
    offset = 0,
  ): Promise<VehicleCatalogListResult<VehicleModel>> {
    const {data} = await vehiclesApi.getVehicleModels(vehicleMakeId, nameContains, offset);
    return {
      data: normalizeVehicleModelsResponse(data),
      meta: normalizeVehicleCatalogMeta(data),
    };
  }

  /** Catalog GETs + provisional POSTs per API: store or update. */
  getCanUseVehicleCatalog(): boolean {
    return (
      meService.getHasPermission("vehicle store") ||
      meService.getHasPermission("vehicle update")
    );
  }

  getIsCreateEnabled() {
    return meService.getHasPermission("vehicle store");
  }

  getIsUpdateEnabled() {
    return meService.getHasPermission("vehicle update");
  }

  getIsDeleteEnabled() {
    return meService.getHasPermission("vehicle delete");
  }

  getIsMergeEnabled() {
    const me = useMeStore.getState().me;
    const isPrivileged = Boolean(me?.isAdmin || me?.isSeniorSupervisor);
    return isPrivileged && this.getIsUpdateEnabled();
  }
}

export const vehiclesService = new VehiclesService();
